import { useRef, useState, type ChangeEvent } from 'react';
import { Button } from '../ui/Button';
import { readBackup, type Loaded } from './restore';
import { RestoreDialog } from './RestoreDialog';

/**
 * Reading a backup back in.
 *
 * Two steps, deliberately: the picker hands the file here, this opens it far
 * enough to know what's inside, and the dialog that follows is where merge or
 * replace gets chosen — by which point it can say what's in the file rather
 * than asking you to remember.
 */
export function RestoreSection() {
  const picker = useRef<HTMLInputElement>(null);

  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  async function onPicked(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Reset first, or picking the same file twice in a row fires nothing.
    event.target.value = '';
    if (file === undefined) return;

    setOpening(true);
    setError(undefined);

    try {
      setLoaded(await readBackup(file));
    } catch (thrown) {
      setError(thrown instanceof Error ? thrown.message : "That file couldn't be opened.");
    } finally {
      setOpening(false);
    }
  }

  return (
    <section className="settings__section">
      <h2 className="section-label">Restore</h2>
      <p className="settings__note">
        Recover your old VisiList from a previous backup file.
      </p>

      <div className="settings__actions">
        <Button variant="quiet" onClick={() => picker.current?.click()} disabled={opening}>
          {opening ? 'Opening…' : 'Restore From A File'}
        </Button>
      </div>

      {error !== undefined && (
        <p className="settings__result" role="alert">
          {error}
        </p>
      )}

      {/*
        `accept` used to be `.visilist,application/zip`, and on an iPhone that
        greyed out the backup — the one file this input exists to read.

        macOS and iOS resolve an `accept` extension through the system's type
        database rather than matching the string. `.visilist` only resolves to
        anything because VisiList.app declares it (see src-tauri/Info.plist),
        and that declaration ships with the *desktop app*. On a phone, which
        has no such app installed, the extension resolves to a throwaway
        dynamic type that matches neither token, so the picker refuses it.
        Hence the tell: restoring worked on the Mac and not on the phone, on
        the same hosted build.

        No token reliably fixes that. `application/octet-stream` looks like it
        should — it maps to `public.data`, which a dynamic type conforms to —
        but that is reasoning, not a measurement, and the thing being reasoned
        about is the one screen standing between someone and their photos. So
        the filter goes entirely, which cannot fail: nothing excluded means
        nothing wrongly excluded.

        The cost is that an unrestricted file input makes iOS offer Photo
        Library and Take Photo alongside Choose File. One extra tap on a
        restore that happens a few times a year, against a picker that refuses
        the only file it wants. If that menu becomes annoying, the narrower
        `application/octet-stream` is the thing to try — but try it on a real
        phone first.

        The filter was only ever a hint. What actually decides is `readBackup`,
        which opens the archive and reads the manifest, and answers "That file
        isn't a VisiList backup" for anything else — so a filter being too
        generous costs nothing, while one being too strict cost everything.
      */}
      <input
        ref={picker}
        type="file"
        className="settings__picker"
        onChange={(event) => void onPicked(event)}
        tabIndex={-1}
      />

      {loaded !== undefined && (
        <RestoreDialog loaded={loaded} onClose={() => setLoaded(undefined)} />
      )}
    </section>
  );
}
